import { STAGE_OPTIONS, STRATEGY_OPTIONS } from "./options";

export function getStageFromStatus(status) {
  for (const stage of STAGE_OPTIONS) {
    const hasStatus = stage.options.some(
      (item) => item.value === status
    );

    if (hasStatus) {
      return stage.key;
    }
  }

  return null;
}

// Shared by getStatusLabel/getStrategyLabel/resolveSelectOption below, so a mutation to the miss
// case reddens every caller rather than whichever function it happened to land in.
function findOption(groups, value) {
  for (const group of groups) {
    const match = group.options.find((item) => item.value === value);
    if (match) return match;
  }
  return null;
}

export function getStatusLabel(status) {
  return findOption(STAGE_OPTIONS, status)?.label ?? status;
}

// Same shape as getStatusLabel above, including its fall-through to the raw value on a miss: most
// production rows already carry a human label rather than the STRATEGY_OPTIONS code, so a miss is
// normal data, not a broken value. The one difference is the empty guard below, which getStatusLabel
// has nowhere at all - this value reaches a grid cell, where an empty string would read as a bug in
// the column rather than as an unset field.
export function getStrategyLabel(strategy) {
  if (!strategy) return "--";
  return findOption(STRATEGY_OPTIONS, strategy)?.label ?? strategy;
}

// A canonical hit returns the real option object (so per-option fields like stageLabel survive);
// a truthy miss gets a synthetic {value, label} pair instead of null, so a real stored value never
// collapses to the placeholder the way getStrategyLabel/getStatusLabel already avoid for their own callers.
export function resolveSelectOption(groups, value, getLabel) {
  if (!value) return null;
  return findOption(groups, value) ?? { value, label: getLabel(value) };
}

// The Pipeline row is always created by the signed-in user (the list endpoint filters on
// createdBy server-side), so a mismatch here is not a real "someone else's row" case — it is
// missing data on the row, and the safe response is no letter rather than a guess.
export function getOwnerInitials(createdBy, user) {
  if (!user?.id || String(createdBy) !== String(user.id)) return "";

  const first = user.firstName?.trim()?.[0] ?? "";
  const last = user.lastName?.trim()?.[0] ?? "";
  if (first || last) return `${first}${last}`.toUpperCase();

  return user.email?.trim()?.[0]?.toUpperCase() ?? "";
}

export function getStageCounts(properties) {
  const counts = {
    all: properties.length,
  };

  STAGE_OPTIONS.forEach((stage) => {
    counts[stage.key] = 0;
  });

  properties.forEach((property) => {
    // property.stage is the backend field (modules/properties/model.js); a property whose stage
    // predates this list (one of the nine removed identifiers, or a legacy display-label string)
    // resolves to null here and counts toward `all` only, not toward any of the three tabs.
    const stage = getStageFromStatus(property.stage);

    if (stage && counts[stage] !== undefined) {
      counts[stage] += 1;
    }
  });

  return counts;
}